<template>
  <div>
    <a-modal
      title="Assign To Client"
      width="600px"
      :visible="visible"
      :destroy-on-close="true"
      @cancel="closeModal"
      v-if="visible"
    >
      <a-form-model
        layout="horizontal"
        ref="form"
        class="flex gap-4"
        :model="form"
        :rules="rules"
        :label-col="{ span: 8 }"
        :wrapper-col="{ span: 16 }"
      >
        <div class="flex flex-column gap-2">
          <a-form-model-item :colon="false" label-align="left" label="Associates" prop="type">
            <search-associates :is-associate-id="true" :multiply="true" :value.sync="form.associate_ids" />
          </a-form-model-item>

          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Position"
            prop="type"
            extra="Please specify the position for the assigned workers to determine their privileges within the client’s company."
          >
            <a-select
              v-model="form.type"
              :dropdown-match-select-width="false"
              :allow-clear="true"
              placeholder="Position"
            >
              <a-select-option v-for="position in positions" :key="position.key" :value="position.key">
                {{ position.title }}
              </a-select-option>
            </a-select>
          </a-form-model-item>
          <a-form-model-item
            :colon="false"
            label-align="left"
            label="Hourly Rate, $"
            extra="Please specify the Hourly Rate that you agreed upon for assigning staff to the facility."
            prop="rate"
          >
            <a-input-number v-model="rateProxy" :min="0" placeholder="Enter rate" />
          </a-form-model-item>

          <a-form-model-item :colon="false" label-align="left" label="Client" prop="id">
            <search-clients :multiply="false" :value.sync="form.id" />
          </a-form-model-item>

          <a-alert
            message="Assign selected persons to the Client’s company with profile data SYNCING. If the person already exists, simply CONNECT their accounts.
          After assignment, DOUBLE-CHECK the Legal Entity information in the Client’s company!"
            type="info"
            show-icon
          />
        </div>
      </a-form-model>

      <template slot="footer">
        <div class="flex justify-content-between">
          <a-button @click="closeModal">Cancel</a-button>
          <a-button @click="handleSubmit" type="primary" :loading="loading">Assign</a-button>
        </div>
      </template>
    </a-modal>

    <a-modal
      title="Assignment Requests"
      width="900px"
      :visible="assignResultVisible"
      :destroy-on-close="true"
      @cancel="closeAssignResultModal"
    >
      <div class="assign-result-content">
        <p class="assign-result-summary">
          Assignment requests sent for review: {{ assignResult.assignmentRequests.length }}
        </p>

        <a-table
          size="small"
          :columns="assignResultColumns"
          :data-source="assignResult.assignmentRequests"
          :pagination="false"
          :row-key="record => record.id"
          :locale="{ emptyText: 'No assignment requests created' }"
          :scroll="{ y: 300, x: 660 }"
        >
          <template slot="worker" slot-scope="text, record">
            {{ record.staff_associate_id }}
          </template>

          <template slot="result" slot-scope="text, record">
            {{ record.created ? 'Created' : 'Already pending' }}
          </template>
        </a-table>
      </div>
      <template slot="footer">
        <div class="flex justify-content-between">
          <a-button @click="closeAssignResultModal"> Cancel </a-button>
        </div>
      </template>
    </a-modal>
  </div>
</template>

<script>
import { Alert, Button, FormModel, InputNumber, notification, Select, Table } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import { deepCopy } from '@/utils/deep-copy'
import clientsAssignSearchVue from './clients-assign-search.vue'
import SearchClients from '@/components/search-clients.vue'

const { mapActions: profileActions } = createNamespacedHelpers('profile')

const initialForm = {
  rate: 0,
  type: undefined,
  associate_ids: [],
  id: undefined
}

const initialRules = {
  rate: [
    {
      required: true,
      message: 'Hourly rate can not be empty',
      trigger: 'change'
    }
  ],
  type: [
    {
      required: true,
      message: 'Position can not be empty',
      trigger: 'change'
    }
  ],
  associate_ids: [
    {
      required: true,
      message: 'Staff can not be empty',
      trigger: 'change',
      type: 'array'
    }
  ],
  id: [
    {
      required: true,
      message: 'Client can not be empty',
      trigger: 'change'
    }
  ]
}

export default {
  components: {
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-alert': Alert,
    'a-input-number': InputNumber,
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-button': Button,
    'a-table': Table,
    'search-clients': SearchClients,
    'search-associates': clientsAssignSearchVue
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    initialValues: {
      type: Object,
      default: () => {
        return { rate: 0, type: undefined, associate_ids: [], id: undefined }
      }
    }
  },
  data() {
    return {
      form: deepCopy(initialForm),
      rules: deepCopy(initialRules),

      positions: [
        { key: 'management', title: 'Management' },
        { key: 'staff', title: 'Staff' }
      ],

      loading: false,
      assignResultVisible: false,
      assignResult: { assignmentRequests: [] },
      assignResultColumns: [
        {
          title: 'Request',
          dataIndex: 'id',
          key: 'id',
          width: 120
        },
        {
          title: 'Worker ID',
          key: 'worker',
          width: 150,
          scopedSlots: { customRender: 'worker' }
        },
        {
          title: 'Status',
          dataIndex: 'status',
          key: 'status',
          width: 160
        },
        {
          title: 'Result',
          key: 'result',
          scopedSlots: { customRender: 'result' }
        }
      ]
    }
  },
  watch: {
    visible: {
      handler(newVal) {
        if (newVal) {
          this.form = deepCopy({ ...initialForm, ...this.initialValues })
        }
      },
      immediate: true
    }
  },
  computed: {
    rateProxy: {
      get() {
        return this.form.rate / 100
      },
      set(value) {
        this.form.rate = Math.round(value * 100)
      }
    }
  },
  methods: {
    ...profileActions(['postAssignAssociateToProjectV2']),

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    closeModal() {
      this.form = deepCopy(initialForm)
      this.$emit('update:visible', false)
    },
    closeAssignResultModal() {
      this.assignResultVisible = false
      this.assignResult = { assignmentRequests: [] }
    },

    handleSubmit() {
      this.$refs.form.validate(async valid => {
        if (!valid) return

        try {
          this.loading = true

          const payload = {
            ...this.form
          }

          const data = await this.postAssignAssociateToProjectV2(payload)
          this.assignResult = { assignmentRequests: data?.assignment_requests ?? [] }
          this.assignResultVisible = true
          this.closeModal()
        } catch (error) {
          this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.ant-row {
  display: flex;
  margin: 0 !important;
}

.ant-form {
  display: flex;
  justify-content: center;
  flex-direction: column;
  gap: 8px;
}

.ant-input-number {
  width: 100%;
}

.assign-result-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.assign-result-summary {
  margin: 0;
}
</style>
